import { sanitizeStreamText } from "./markdown";

export const TOKEN_WORD = "word";
export const TOKEN_BREAK = "break";
export const WORD_CLASS = "ai-chat-stream__word";
export const CARET_CLASS = "ai-chat-stream__caret";
export const WORD_MS = 55;
export const STREAM_COUNT_ATTR = "streamCount";
export const STREAM_STARTED_ATTR = "streamStarted";
export const CHUNK_INDEX_ATTR = "askScanChunk";

export function tokenizeStreamingText(text) {
  const tokens = [];
  let buffer = "";
  const safeText = sanitizeStreamText(text);

  const flushWord = () => {
    if (!buffer) {
      return;
    }
    tokens.push({ kind: TOKEN_WORD, text: buffer });
    buffer = "";
  };

  Array.from(safeText).forEach((char) => {
    if (char === "\n") {
      flushWord();
      tokens.push({ kind: TOKEN_BREAK });
      return;
    }
    if (/\s/.test(char)) {
      flushWord();
      return;
    }
    buffer += char;
  });
  flushWord();

  return tokens;
}

function prefersReducedMotion() {
  return Boolean(
    typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export function createStreamingPlayer({
  onFrame,
  wordMs = WORD_MS,
  reducedMotion = prefersReducedMotion(),
} = {}) {
  let tokens = [];
  let count = 0;
  let timer = null;
  let caughtUpCallbacks = [];

  const emit = () => {
    if (typeof onFrame === "function") {
      onFrame({
        tokens,
        count,
        done: tokens.length > 0 && count >= tokens.length,
      });
    }
  };

  const flushCaughtUp = () => {
    if (tokens.length > 0 && count < tokens.length) {
      return;
    }
    const callbacks = caughtUpCallbacks;
    caughtUpCallbacks = [];
    callbacks.forEach((callback) => callback());
  };

  const schedule = () => {
    if (timer != null) {
      return;
    }
    if (count >= tokens.length) {
      flushCaughtUp();
      return;
    }
    if (reducedMotion) {
      count = tokens.length;
      emit();
      flushCaughtUp();
      return;
    }
    timer = setTimeout(() => {
      timer = null;
      count += 1;
      emit();
      schedule();
    }, wordMs);
  };

  return {
    setTokens(nextTokens) {
      const incoming = Array.isArray(nextTokens) ? nextTokens : [];
      const previousTokens = tokens;
      const paintedCount = count;
      tokens = incoming;
      if (count > tokens.length) {
        count = tokens.length;
      }
      const prefixChanged = !paintedPrefixMatches(
        previousTokens,
        tokens,
        paintedCount
      );
      if (prefixChanged && timer != null) {
        clearTimeout(timer);
        timer = null;
      }
      if (count === 0 && tokens.length > 0) {
        count = 1;
        emit();
      } else if (prefixChanged) {
        emit();
      }
      schedule();
    },
    getCount() {
      return count;
    },
    stop() {
      if (timer != null) {
        clearTimeout(timer);
        timer = null;
      }
      caughtUpCallbacks = [];
    },
    whenCaughtUp(callback) {
      if (typeof callback !== "function") {
        return;
      }
      if (tokens.length === 0 || count >= tokens.length) {
        callback();
        return;
      }
      caughtUpCallbacks.push(callback);
    },
  };
}

const FIRST_CHUNK_INDEX = 0;

export function contiguousStreamText(raw) {
  if (!raw) {
    return "";
  }

  const spans = raw.querySelectorAll(`[data-${chunkDataAttribute()}]`);
  if (spans.length === 0) {
    return raw.textContent ?? "";
  }

  const byIndex = new Map();
  spans.forEach((span) => {
    const index = Number(span.dataset[CHUNK_INDEX_ATTR]);
    if (!Number.isInteger(index) || index < FIRST_CHUNK_INDEX) {
      return;
    }
    byIndex.set(index, span.textContent ?? "");
  });

  let text = "";
  let index = FIRST_CHUNK_INDEX;
  while (byIndex.has(index)) {
    text += byIndex.get(index);
    index += 1;
  }
  return text;
}

function chunkDataAttribute() {
  return CHUNK_INDEX_ATTR.replace(
    /[A-Z]/g,
    (letter) => `-${letter.toLowerCase()}`
  );
}

function tokenNode(token) {
  if (token.kind === TOKEN_BREAK) {
    return document.createElement("br");
  }
  const span = document.createElement("span");
  span.className = WORD_CLASS;
  span.textContent = `${token.text} `;
  return span;
}

function ensureCaret(target) {
  let caret = target.querySelector(`.${CARET_CLASS}`);
  if (caret) {
    return caret;
  }
  caret = document.createElement("span");
  caret.className = CARET_CLASS;
  caret.setAttribute("aria-hidden", "true");
  target.appendChild(caret);
  return caret;
}

export function renderStreamingFrame(
  target,
  tokens,
  count,
  { showCaret = true } = {}
) {
  if (!target) {
    return 0;
  }

  if (target.dataset[STREAM_STARTED_ATTR] !== "true") {
    target.replaceChildren();
    target.dataset[STREAM_STARTED_ATTR] = "true";
    target.dataset[STREAM_COUNT_ATTR] = "0";
  }

  let previous = Number(target.dataset[STREAM_COUNT_ATTR] || 0);
  const nextCount = Math.max(0, Math.min(count, tokens.length));
  if (previous > 0 && !renderedPrefixMatches(target, tokens, previous)) {
    target.replaceChildren();
    previous = 0;
    target.dataset[STREAM_COUNT_ATTR] = "0";
  }
  const caret = showCaret
    ? ensureCaret(target)
    : target.querySelector(`.${CARET_CLASS}`);

  for (let index = previous; index < nextCount; index += 1) {
    const token = tokens[index];
    if (!token) {
      break;
    }
    const node = tokenNode(token);
    if (caret && caret.parentNode === target) {
      target.insertBefore(node, caret);
    } else {
      target.appendChild(node);
    }
  }

  target.dataset[STREAM_COUNT_ATTR] = String(nextCount);

  if (!showCaret && caret) {
    caret.remove();
  }

  return nextCount;
}

function renderedNodes(target) {
  return [...target.childNodes].filter(
    (node) => !(node.classList && node.classList.contains(CARET_CLASS))
  );
}

function sameToken(left, right) {
  if (!left || !right || left.kind !== right.kind) {
    return false;
  }
  if (left.kind === TOKEN_BREAK) {
    return true;
  }
  return left.text === right.text;
}

function paintedPrefixMatches(previousTokens, nextTokens, paintedCount) {
  if (paintedCount > nextTokens.length) {
    return false;
  }
  const compared = Math.min(paintedCount, previousTokens.length);
  for (let index = 0; index < compared; index += 1) {
    if (!sameToken(previousTokens[index], nextTokens[index])) {
      return false;
    }
  }
  return true;
}

function renderedPrefixMatches(target, tokens, count) {
  const nodes = renderedNodes(target);
  if (nodes.length !== count) {
    return false;
  }
  for (let index = 0; index < count; index += 1) {
    const token = tokens[index];
    const node = nodes[index];
    if (!token || !node) {
      return false;
    }
    if (token.kind === TOKEN_BREAK) {
      if (node.tagName !== "BR") {
        return false;
      }
    } else if (node.textContent !== `${token.text} `) {
      return false;
    }
  }
  return true;
}
